import { z } from 'zod';
import { COMMON_TONES, TONES } from './options';

/**
 * The menus a writer keeps in order: AI tools, tones, and saved instructions. In each, a choice,
 * built-in or the writer's own, can be hidden from the menu or pinned to its top.
 */
export const MENUS = ['tools', 'tones', 'instructions'] as const;

export type Menu = (typeof MENUS)[number];

/**
 * The built-in choices each menu starts without. Only the rarer tones start hidden; all AI
 * tools and saved instructions start shown, until the writer changes their menu settings.
 */
const STARTS_HIDDEN: Readonly<Record<Menu, ReadonlySet<string>>> = {
  tools: new Set(),
  tones: new Set(TONES.filter((tone) => !COMMON_TONES.includes(tone))),
  instructions: new Set(),
};

/** Whether a choice starts hidden from its menu, until the writer shows it. */
export const startsHidden = (menu: Menu, item: string): boolean => STARTS_HIDDEN[menu].has(item);

/** How a writer keeps one choice in a menu. */
export const MenuSettingInputSchema = z
  .object({
    hidden: z.boolean().describe('Left out of the menu, though it still works when asked for.'),
    pinned: z.boolean().describe('Listed first in the menu.'),
  })
  .meta({ id: 'MenuSettingInput' });

export type MenuSettingInput = z.infer<typeof MenuSettingInputSchema>;

export const MenuSettingSchema = MenuSettingInputSchema.extend({
  id: z.string().describe('A built-in choice by name, or one of the caller’s own by id.'),
}).meta({ id: 'MenuSetting' });

export type MenuSetting = z.infer<typeof MenuSettingSchema>;

export const MenuSettingResponseSchema = z
  .object({ setting: MenuSettingSchema })
  .meta({ id: 'MenuSettingResponse' });

export const MenuSettingsResponseSchema = z
  .object({
    settings: z
      .array(MenuSettingSchema)
      .describe(
        'Only choices kept otherwise than shown and unpinned, including built-in ones that start hidden.',
      ),
  })
  .meta({ id: 'MenuSettings' });

/**
 * Each menu's settings as every writer starts, before they change any: its built-in choices that
 * start hidden. Without an account, the menus stay this way.
 */
const starting = (menu: Menu): readonly MenuSetting[] =>
  [...STARTS_HIDDEN[menu]].map((id) => ({ id, hidden: true, pinned: false }));

export const STARTING_SETTINGS: Readonly<Record<Menu, readonly MenuSetting[]>> = {
  tools: starting('tools'),
  tones: starting('tones'),
  instructions: starting('instructions'),
};
